import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClearIcon from '@mui/icons-material/Clear';
import {
  EPISODE_FILTER_OPERATORS,
  EPISODE_OUTPUT_META,
  EPISODE_OUTPUT_OPTIONS,
  episodeOutputDescription,
  episodeOutputFields,
} from '../../utils/episodeOutputMeta';
import {
  formatEpisodeDurationValue,
} from '../../utils/durationFormat';

function fieldByFinalName(fields, finalName) {
  return fields.find((f) => f.value === finalName) || null;
}

function columnDisplayName(field) {
  return String(field?.value || '').replace(/_/g, ' ');
}

/**
 * Tabla interactiva del resultado de episodios: renombrar columnas, ordenar,
 * filtrar y limitar (Top N). Los cambios se guardan en episodeAnalysis.resultOps
 * y columnLabels, y el servidor los aplica al publicar/previsualizar.
 */
export default function EpisodeResultsTable({
  data = [],
  loading = false,
  error = '',
  episodeAnalysis,
  groupColumns = [],
  onChangeEpisodeAnalysis,
  maxPreviewRows = 200,
}) {
  const output = episodeAnalysis?.output || 'episodes';
  const outputHint = EPISODE_OUTPUT_OPTIONS.find((o) => o.value === output)?.hint || '';

  const fields = useMemo(
    () => episodeOutputFields(episodeAnalysis, groupColumns),
    [episodeAnalysis, groupColumns]
  );
  const fieldKeys = useMemo(() => fields.map((f) => f.value), [fields]);

  const resultOps = episodeAnalysis?.resultOps || {};
  const sortField = resultOps.sortField || '';
  const sortDir = resultOps.sortDir || EPISODE_OUTPUT_META[output]?.defaultSort?.dir || 'desc';
  const filters = Array.isArray(resultOps.filters) ? resultOps.filters : [];
  const limit = resultOps.limit ?? '';

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuField, setMenuField] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [filterOp, setFilterOp] = useState('gte');
  const [filterVal, setFilterVal] = useState('');

  useEffect(() => {
    if (!menuField) return;
    setRenameDraft(episodeAnalysis?.columnLabels?.[menuField.base] || '');
    const existing = filters.find((f) => f.field === menuField.value);
    setFilterOp(existing?.operator || 'gte');
    setFilterVal(existing?.value ?? '');
  }, [menuField, episodeAnalysis?.columnLabels, filters]);

  const patchEpisode = (patch) => {
    onChangeEpisodeAnalysis?.({ ...(episodeAnalysis || {}), ...patch });
  };

  const patchResultOps = (patch) => {
    patchEpisode({ resultOps: { ...resultOps, ...patch } });
  };

  const openMenu = (event, field) => {
    setMenuAnchor(event.currentTarget);
    setMenuField(field);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuField(null);
  };

  const commitRename = () => {
    if (!menuField) return;
    const next = String(renameDraft || '').trim();
    const labels = { ...(episodeAnalysis?.columnLabels || {}) };
    if (next && next !== menuField.base) labels[menuField.base] = next;
    else delete labels[menuField.base];
    patchEpisode({ columnLabels: labels });
  };

  const applySort = (dir) => {
    if (!menuField) return;
    patchResultOps({ sortField: menuField.value, sortDir: dir });
    closeMenu();
  };

  const applyFilter = () => {
    if (!menuField || filterVal === '') return;
    const rest = filters.filter((f) => f.field !== menuField.value);
    patchResultOps({
      filters: [...rest, { field: menuField.value, operator: filterOp, value: filterVal }],
    });
    closeMenu();
  };

  const removeFilter = (fieldFinal) => {
    patchResultOps({ filters: filters.filter((f) => f.field !== fieldFinal) });
  };

  const clearAllFilters = () => patchResultOps({ filters: [] });

  const activeFilterFor = (fieldFinal) => filters.find((f) => f.field === fieldFinal);

  const formatCell = (row, field) => {
    const raw = row[field.value];
    if (raw == null || raw === '') return '';
    if (field.duration) {
      return formatEpisodeDurationValue(raw, episodeAnalysis, field.base, { tooltip: false });
    }
    return String(raw);
  };

  const cellTitle = (row, field) => {
    if (!field.duration) return undefined;
    const raw = row[field.value];
    if (raw == null || raw === '') return undefined;
    return formatEpisodeDurationValue(raw, episodeAnalysis, field.base, { tooltip: true });
  };

  return (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minHeight: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
    >
      {loading && (
        <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }} />
      )}

      <Box
        sx={{
          px: 1,
          py: 0.75,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Resultado episodios
        </Typography>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Formato de resultado</InputLabel>
          <Select
            label="Formato de resultado"
            value={output}
            onChange={(e) => patchEpisode({ output: e.target.value })}
          >
            {EPISODE_OUTPUT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {outputHint && (
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
            {outputHint}
          </Typography>
        )}
        <TextField
          size="small"
          type="number"
          label="Top N"
          placeholder="Todos"
          value={limit}
          onChange={(e) => {
            const v = e.target.value.trim();
            patchResultOps({ limit: v === '' ? undefined : Math.max(1, parseInt(v, 10) || 1) });
          }}
          inputProps={{ min: 1 }}
          sx={{ width: 90 }}
        />
        {filters.map((f) => {
          const fld = fieldByFinalName(fields, f.field);
          const label = fld?.value || f.field;
          return (
            <Chip
              key={`${f.field}-${f.operator}-${f.value}`}
              size="small"
              label={`${label} ${f.operator} ${f.value}`}
              onDelete={() => removeFilter(f.field)}
            />
          );
        })}
        {filters.length > 0 && (
          <Button size="small" startIcon={<ClearIcon fontSize="small" />} onClick={clearAllFilters}>
            Quitar filtros
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          Clic en columna: renombrar, ordenar o filtrar ({episodeOutputDescription(output)})
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ m: 1, py: 0.25 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {fields.map((f) => {
                const sorted = sortField === f.value;
                const filter = activeFilterFor(f.value);
                const renamed = Boolean(episodeAnalysis?.columnLabels?.[f.base]);
                return (
                  <TableCell
                    key={f.value}
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', minWidth: 90, bgcolor: 'grey.100' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Box
                        sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={(e) => openMenu(e, f)}
                      >
                        {f.isGroup && (
                          <Chip size="small" label="Grupo" sx={{ height: 18, fontSize: 10, mb: 0.25 }} />
                        )}
                        <Typography variant="body2" fontWeight={600} noWrap title={f.base}>
                          {columnDisplayName(f)}
                          {sorted && (
                            <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                              {sortDir === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </Typography>
                        {renamed && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {f.base.replace(/_/g, ' ')}
                          </Typography>
                        )}
                        {filter && (
                          <Tooltip title={`Filtro: ${filter.operator} ${filter.value}`}>
                            <FilterListIcon sx={{ fontSize: 14, color: 'secondary.main', verticalAlign: 'middle' }} />
                          </Tooltip>
                        )}
                      </Box>
                      <IconButton size="small" onClick={(e) => openMenu(e, f)} aria-label={`Opciones ${f.value}`}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {(data || []).slice(0, maxPreviewRows).map((row, ri) => (
              <TableRow key={ri} hover>
                {fields.map((f) => (
                  <TableCell key={f.value} sx={{ whiteSpace: 'nowrap' }} title={cellTitle(row, f)}>
                    {formatCell(row, f)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && !(data || []).length && (
              <TableRow>
                <TableCell colSpan={Math.max(1, fieldKeys.length)}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Sin episodios detectados con los parámetros actuales.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(menuAnchor && menuField)}
        anchorEl={menuAnchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {menuField && (
          <Box sx={{ p: 2, width: 280, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Typography variant="subtitle2">{columnDisplayName(menuField)}</Typography>
            <TextField
              size="small"
              fullWidth
              label="Nombre de columna"
              placeholder={menuField.base.replace(/_/g, ' ')}
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitRename();
                }
              }}
            />
            <Divider />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                startIcon={<ArrowUpwardIcon />}
                variant={sortField === menuField.value && sortDir === 'asc' ? 'contained' : 'outlined'}
                onClick={() => applySort('asc')}
              >
                Asc
              </Button>
              <Button
                size="small"
                startIcon={<ArrowDownwardIcon />}
                variant={sortField === menuField.value && sortDir === 'desc' ? 'contained' : 'outlined'}
                onClick={() => applySort('desc')}
              >
                Desc
              </Button>
            </Box>
            <Divider />
            <FormControl size="small" fullWidth>
              <InputLabel>Filtro</InputLabel>
              <Select label="Filtro" value={filterOp} onChange={(e) => setFilterOp(e.target.value)}>
                {EPISODE_FILTER_OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label="Valor"
              value={filterVal}
              onChange={(e) => setFilterVal(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" onClick={applyFilter} disabled={filterVal === ''}>
                Aplicar filtro
              </Button>
              {activeFilterFor(menuField.value) && (
                <Button size="small" onClick={() => { removeFilter(menuField.value); closeMenu(); }}>
                  Quitar
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Popover>
    </Paper>
  );
}
